import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BaseComponent } from '../../../base.component';
import { combineLatest, filter, finalize, iif, map, Observable, of, switchMap, takeUntil } from 'rxjs';
import { CriterionsService } from '../../../services/criterions.service';
import { AnswerValidator, EditGridRequiredValidator } from '../../../validators';
import { ICriterion } from '../../../../distribute-team-members/models/criterion';
import { DecisionInput } from '../../../models/decision-input.model';
import { CriterionInformation } from '../../../models/criterion-information.mode';
import _cloneDeep from 'lodash/cloneDeep';
import { showToast } from '../../../../toast.helper';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { CopyModel } from '../../../models/copy.model';
import { ExecuteVisitType } from '../../../models/execute-visit.model';
import { FormioComponent } from 'dp-formio';
import { CustomEditGridComponent } from 'dp-formio';
import { ItimadCriterionsService } from '../../../services/itimad-criterions.service';

@Component({
  selector: 'app-question-decision',
  templateUrl: './question-decision.component.html',
  styleUrls: ['./question-decision.component.scss']
})
export class QuestionDecisionComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() memberId: string;
  @Input() decisionPreview: boolean;
  @ViewChild(FormioComponent) formioComponent: FormioComponent;
  isLoadingResults: boolean;
  process$ = this.criterionsService.process$;
  question$ = this.criterionsService.questionResult$;
  answers$: Observable<Array<{ value: string, tooltipAr: string, tooltipEn: string, hasTooltip: boolean }>>;
  attachmentFormKey = this.criterionsService.forms.attachmentsFormKey;
  form: FormGroup;

  criterion: ICriterion;
  decisionInput: DecisionInput;
  modelId: number;

  get attachmentsControl() { return this.form.get('attachments'); }

  get isInstitutionEntries() {
    return this.decisionInput.executeVisitType == ExecuteVisitType.InstitutionEntries;
  }

  get isFinalReportWithInstitutionEntries() {
    return this.decisionInput.executeVisitType == ExecuteVisitType.FinalReportWithInstitutionEntries;
  }

  get isFinalReport() {
    return !this.decisionPreview && (this.decisionInput.executeVisitType == ExecuteVisitType.FinalReport ||
      this.decisionInput.executeVisitType == ExecuteVisitType.InstitutionEntries ||
      this.decisionInput.executeVisitType == ExecuteVisitType.FinalReportWithInstitutionEntries);
  }

  get enabledCopy() {
    return !!this.memberId;
  }
  get isFormDisabled() {
    return !this.decisionInput.enableEdit ||
      this.enabledCopy ||
      this.isInstitutionEntries ||
      this.isFinalReportWithInstitutionEntries;
  }

  constructor(private formBuilder: FormBuilder,
    private criterionsService: CriterionsService,
    private itimadCriterionsService: ItimadCriterionsService,
    private toastrService: ToastrService,
    private translateService: TranslateService) { super(); }

  ngOnInit() {
    this.initAnswers();
    combineLatest([
      this.criterionsService.criterion$
        .pipe(filter(data => data.criterionType == 'STICK')),
      this.criterionsService.decisionInputs$
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([criterion, inputs]) => {
        this.criterion = criterion;
        this.decisionInput = inputs;
        this.form?.reset();
        this.initForm();
        this.registerUpdateFormValidation();
        this.getData();
        this.registerCopyEvaluation();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.memberId?.previousValue && changes.memberId?.previousValue != changes.memberId?.currentValue) {
      this.getData();
    }
  }

  copy(controlName: string) {
    const value = this.form.controls[controlName].value;
    this.criterionsService.sendEvaluation$.next(new CopyModel(controlName, value));
    if (controlName == 'termsOfTheRulingWithEvidence') {
      this.criterionsService.sendEvaluation$.next(new CopyModel('attachments', this.attachmentsControl.value));
    }
  }

  submit() {
    if (this.form.valid) {
      this.isLoadingResults = true;
      const value = _cloneDeep(this.form.value);
      delete value.question;

      const data: CriterionInformation = {
        id: this.modelId,
        criterionId: this.criterion.id,
        criterionKey: this.criterion.criterionKey,
        visitKey: this.decisionInput.visitMetaData.visitKey,
        memberId: this.decisionInput.visitMetaData.memberId,
        ...value,
        isApply: !value.notApply,
        evidences: value.attachments.map(item => { return { files: item.file, fileName: item.fileName }; })
      };

      of(!!this.modelId)
        .pipe(
          switchMap((isUpdate) =>
            iif(
              () => isUpdate,
              this.itimadCriterionsService.editCriterionInformation(data, this.isFinalReport),
              this.itimadCriterionsService.createCriterionInformation(data, this.isFinalReport)
            )
          ),
          finalize(() => this.isLoadingResults = false)
        )
        .subscribe(model => {
          this._updateForm(model);
          this.criterionsService.decisionTaken$.next();
          showToast(this.toastrService,
            this.translateService.instant("forms.common.Criteria.Saved done"),
            this.translateService.instant("forms.common.Criteria.Saved Successfully"),
            false
          );
        });
    }
  }

  registerFormioEvent(formSettings: any) {
    this.attachmentsControl.updateValueAndValidity();
    this.formioComponent.formio.events.on('formio.change', (event) => {
      if (event.changed?.instance instanceof CustomEditGridComponent)
        this.attachmentsControl.updateValueAndValidity();
    });
  }

  private initForm() {
    this.form = this.formBuilder.group({
      question: [this.question$],
      notes: [{ value: '', disabled: this.isFormDisabled }],
      conditions: [{ value: '', disabled: this.isFormDisabled }],
      notApply: [{ value: false, disabled: this.isFormDisabled }],
      reviewerEvaluation: [{ value: '', disabled: this.isFormDisabled }],
      termsOfTheRulingWithEvidence: [{ value: '', disabled: this.isFormDisabled }, [Validators.required]],
      attachments: [[], [Validators.required, EditGridRequiredValidator]],
      summaryOfRecommendations: [{ value: '', disabled: this.isFormDisabled }, [Validators.required]],
      summaryOfCommendations: [{ value: '', disabled: this.isFormDisabled }, [Validators.required]],
      summaryOfSuggestions: [{ value: '', disabled: this.isFormDisabled }, [Validators.required]],
      executiveSummary: [{ value: '', disabled: this.isFormDisabled }, [Validators.required]],
    }, {
      validators: [AnswerValidator()]
    });
  }
  private registerUpdateFormValidation() {
    const controls = ['termsOfTheRulingWithEvidence', 'summaryOfRecommendations', 'summaryOfCommendations', 'summaryOfSuggestions', 'executiveSummary'];
    this.form.controls.notApply.valueChanges.subscribe(checked => {
      controls.forEach(key => {
        if (!checked) {
          this.form.get(key).setValidators([Validators.required]);
        } else {
          this.form.get(key).clearValidators();
        }
        this.form.get(key).updateValueAndValidity();
      });
    });
    this.form.controls.termsOfTheRulingWithEvidence.valueChanges.subscribe(value => {
      if (!!value) {
        this.attachmentsControl.setValidators([Validators.required, EditGridRequiredValidator]);
      } else {
        this.attachmentsControl.setValue([]);
        this.attachmentsControl.clearValidators();
      }
      this.attachmentsControl.updateValueAndValidity();
    });
  }

  private initAnswers() {
    this.answers$ = combineLatest([this.question$, this.criterionsService.process$])
      .pipe(
        filter(([question, process]) => !!question && !!process),
        map(([question, process]) => {
          if (process.answerMaxScore) {
            const generalToolTip = question.answerTooltip ? question.answerTooltip.find(q => !q.score) : { tooltip: '' };
            return Array.from({ length: process.answerMaxScore }, (_, i) => {
              const answerTooltip = generalToolTip ?? question.answerTooltip.find(q => q.score == i + 1);
              return {
                value: `${i + 1}`,
                tooltipAr: answerTooltip?.tooltipAr,
                tooltipEn: answerTooltip?.tooltipEn,
                hasTooltip: !!answerTooltip?.tooltipAr || !!answerTooltip?.tooltipEn
              }
            });
          }
        }));
  }

  private getData() {
    this.isLoadingResults = true;
    this.itimadCriterionsService.getCriterionInformation(
      this.decisionInput.visitMetaData.visitKey,
      this.criterion.criterionKey,
      this.memberId || this.decisionInput.currentMemberId,
      this.isFinalReport)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.isLoadingResults = false)
      )
      .subscribe(criterionInformation => {
        this._updateForm(criterionInformation);
      });
  }
  private _updateForm(data: CriterionInformation) {
    this.modelId = data?.id;
    this.form.patchValue({
      notes: data?.notes,
      conditions: data?.conditions,
      notApply: !data?.isApply,
      reviewerEvaluation: data?.reviewerEvaluation?.toString(),
      termsOfTheRulingWithEvidence: data?.termsOfTheRulingWithEvidence,
      attachments: data?.evidences?.map(item => { return { file: item.files, fileName: item.fileName }; }) || [],
      summaryOfRecommendations: data?.summaryOfRecommendations,
      summaryOfCommendations: data?.summaryOfCommendations,
      summaryOfSuggestions: data?.summaryOfSuggestions,
      executiveSummary: data?.executiveSummary
    });
  }

  private registerCopyEvaluation() {
    if (!this.enabledCopy)
      this.criterionsService.sendEvaluation$
        .pipe(
          filter(data => !!data),
          takeUntil(this.destroy$),
        )
        .subscribe(data => {
          if (data.key == 'attachments') {
            data.value?.forEach(item => {
              item.file?.forEach(file => {
                delete file.id;
              });
            });
          }
          this.form.patchValue({
            [data.key]: data.value
          });
        });

  }

}